/**
 * Authentication service
 * Handles all authentication-related API calls
 */

import type { LoginCredentials, LoginResponse, User } from '@/types/auth';
import type { ISODateString } from '@/types/api';
import { HttpError } from '@/utils/httpClient';

// Mock users for development
const MOCK_USERS = [
    {
        id: '1',
        email: 'admin@biosam.com',
        password: 'admin123',
        firstName: 'Admin',
        lastName: 'Biosam',
        role: 'admin' as const,
        createdAt: '2024-01-01T00:00:00Z' as ISODateString,
    },
    {
        id: '2',
        email: 'tecnico@biosam.com',
        password: 'tecnico123',
        firstName: 'Juan',
        lastName: 'Técnico',
        role: 'technician' as const,
        createdAt: '2024-01-01T00:00:00Z' as ISODateString,
    },
    {
        id: '3',
        email: 'cliente@hotelmeridian.com',
        password: 'cliente123',
        firstName: 'Hotel',
        lastName: 'Meridian',
        role: 'client' as const,
        createdAt: '2024-01-01T00:00:00Z' as ISODateString,
    },
];

// Helper to simulate API delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const TOKEN_KEY = 'biosam_access_token';
const REFRESH_TOKEN_KEY = 'biosam_refresh_token';

/**
 * Store authentication tokens
 */
function storeTokens(accessToken: string, refreshToken: string, rememberMe: boolean): void {
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem(TOKEN_KEY, accessToken);
    storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

/**
 * Clear stored tokens
 */
function clearTokens(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Get stored access token
 */
export function getAccessToken(): string | null {
    return sessionStorage.getItem(TOKEN_KEY) ?? localStorage.getItem(TOKEN_KEY);
}

/**
 * Get stored refresh token
 */
export function getRefreshToken(): string | null {
    return sessionStorage.getItem(REFRESH_TOKEN_KEY) ?? localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Check if user has stored tokens
 */
export function hasStoredTokens(): boolean {
    return getAccessToken() !== null;
}

/**
 * Authentication service
 */
export const authService = {
    /**
     * Login with email and password (MOCKED)
     */
    async login(credentials: LoginCredentials): Promise<LoginResponse> {
        // Simulate API delay
        await delay(800);

        // Find user
        const user = MOCK_USERS.find(
            u => u.email === credentials.email && u.password === credentials.password
        );

        if (!user) {
            throw new HttpError(401, 'Credenciales inválidas', { code: 'INVALID_CREDENTIALS', message: 'Credenciales inválidas', timestamp: new Date().toISOString() });
        }

        // Generate mock tokens
        const accessToken = `mock_access_token_${user.id}_${Date.now()}`;
        const refreshToken = `mock_refresh_token_${user.id}_${Date.now()}`;

        // Store tokens
        storeTokens(accessToken, refreshToken, credentials.rememberMe ?? false);

        // Store user data
        const userData: User = {
            id: user.id,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            lastLoginAt: new Date().toISOString() as ISODateString,
            createdAt: user.createdAt || new Date().toISOString() as ISODateString,
        };

        const storage = credentials.rememberMe ? localStorage : sessionStorage;
        storage.setItem('biosam_user', JSON.stringify(userData));

        return {
            accessToken,
            refreshToken,
            user: userData,
            expiresAt: new Date(Date.now() + 3600000).toISOString() as ISODateString,
        };
    },

    /**
     * Logout current user (MOCKED)
     */
    async logout(): Promise<void> {
        await delay(300);
        clearTokens();
        localStorage.removeItem('biosam_user');
        sessionStorage.removeItem('biosam_user');
    },

    /**
     * Get current authenticated user (MOCKED)
     */
    async getCurrentUser(): Promise<User> {
        const token = getAccessToken();

        if (!token) {
            throw new HttpError(401, 'No authentication token found', { code: 'UNAUTHORIZED', message: 'No authentication token found', timestamp: new Date().toISOString() });
        }

        await delay(200);

        // Get stored user
        const userStr = sessionStorage.getItem('biosam_user') ?? localStorage.getItem('biosam_user');

        if (!userStr) {
            throw new HttpError(401, 'User not found', { code: 'UNAUTHORIZED', message: 'User not found', timestamp: new Date().toISOString() });
        }

        return JSON.parse(userStr) as User;
    },

    /**
     * Refresh access token (MOCKED)
     */
    async refreshToken(): Promise<LoginResponse> {
        const refreshToken = getRefreshToken();

        if (!refreshToken) {
            throw new HttpError(401, 'No refresh token found', { code: 'UNAUTHORIZED', message: 'No refresh token found', timestamp: new Date().toISOString() });
        }

        await delay(300);

        // Extract user id from mock token
        const userId = refreshToken.split('_')[3];
        const user = MOCK_USERS.find(u => u.id === userId);

        if (!user) {
            throw new HttpError(401, 'Invalid refresh token', { code: 'UNAUTHORIZED', message: 'Invalid refresh token', timestamp: new Date().toISOString() });
        }

        // Generate new mock tokens
        const newAccessToken = `mock_access_token_${user.id}_${Date.now()}`;
        const newRefreshToken = `mock_refresh_token_${user.id}_${Date.now()}`;

        // Store new tokens
        const isRemembered = localStorage.getItem(TOKEN_KEY) !== null;
        storeTokens(newAccessToken, newRefreshToken, isRemembered);

        const userData: User = {
            id: user.id,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            lastLoginAt: new Date().toISOString() as ISODateString,
            createdAt: user.createdAt || new Date().toISOString() as ISODateString,
        };

        return {
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
            user: userData,
            expiresAt: new Date(Date.now() + 3600000).toISOString() as ISODateString,
        };
    },

    /**
     * Clear all authentication data
     */
    clearAuth(): void {
        clearTokens();
    },
} as const;

export default authService;
